/**
 * Meşgul örtüsü (bkz. busyStore): logo, başlık, ilerleme çubuğu + "120 / 523", sırayla değişen
 * açıklama ve varsa "Durdur".
 *
 * Kısa işlemlerde görünmez: ilk 300 ms içinde biten işlem örtü çizdirmez — her silmede ekranın
 * bir an kararıp açılması, beklemekten daha rahatsız ediciydi.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OctoMark } from './OctoMark';
import { Button } from './Button';
import { cn } from '@/shared/lib/cn';
import { useBusyStore, type BusyTask } from './busyStore';

const SHOW_AFTER_MS = 300;
const HINT_STEP_MS = 2800;

function useTaskFor(scope: string | null): BusyTask | null {
  return useBusyStore((s) => {
    for (let i = s.tasks.length - 1; i >= 0; i--) if (s.tasks[i]!.scope === scope) return s.tasks[i]!;
    return null;
  });
}

function BusyPanel({ task }: { task: BusyTask }) {
  const { t } = useTranslation();
  const [hint, setHint] = useState(0);
  // Açıklamalar ilerler ve sonuncusunda kalır (başa sarmak "yeniden başladı" hissi veriyor).
  // Açıklama listesi değişince panel yeniden bağlanır (anahtar), sıra baştan başlar.
  useEffect(() => {
    if (task.hints.length <= 1) return;
    const timer = setInterval(() => setHint((i) => (i + 1 < task.hints.length ? i + 1 : i)), HINT_STEP_MS);
    return () => clearInterval(timer);
  }, [task.hints]);

  const hasProgress = task.total != null && task.total > 0 && task.done != null;
  const pct = hasProgress ? Math.min(100, Math.round((task.done! / task.total!) * 100)) : 0;

  return (
    <div className="bg-bg-elevated border-border flex w-[min(22rem,90vw)] flex-col items-center gap-3 rounded-lg border px-6 py-5 shadow-[var(--shadow)]">
      <OctoMark className="octo-loader-mark h-12 w-12" />
      <p className="text-fg text-center text-sm font-semibold">{task.title}</p>
      {hasProgress ? (
        <div className="flex w-full flex-col gap-1">
          <div
            className="bg-bg-muted h-1.5 w-full overflow-hidden rounded-full"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={task.total!}
            aria-valuenow={task.done!}
          >
            <div className="bg-brand-600 h-full rounded-full transition-[width] duration-300" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-fg-muted text-center text-xs tabular-nums">
            {t('busy.progress', { done: task.done, total: task.total })}
          </p>
        </div>
      ) : null}
      {task.hints.length ? (
        <p key={hint} className="text-fg-muted octo-loader-text min-h-4 text-center text-xs" aria-live="polite">
          {task.cancelling ? t('busy.cancelling') : task.hints[Math.min(hint, task.hints.length - 1)]}
        </p>
      ) : task.cancelling ? (
        <p className="text-fg-muted text-center text-xs">{t('busy.cancelling')}</p>
      ) : null}
      {task.onCancel ? (
        <Button size="sm" variant="secondary" onClick={task.onCancel} disabled={task.cancelling}>
          {t('busy.cancel')}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * `scope`: sekme kimliği (örtü o sekmenin kabını kaplar) ya da null (tüm uygulama, sabit).
 */
export function BusyOverlay({ scope }: { scope: string | null }) {
  const task = useTaskFor(scope);
  const [visibleId, setVisibleId] = useState<number | null>(null);

  const taskId = task?.id ?? null;
  const startedAt = task?.startedAt ?? 0;
  useEffect(() => {
    if (taskId === null) return;
    const timer = setTimeout(() => setVisibleId(taskId), Math.max(0, SHOW_AFTER_MS - (Date.now() - startedAt)));
    return () => clearTimeout(timer);
  }, [taskId, startedAt]);

  if (!task || visibleId !== task.id) return null;
  return (
    <div
      className={cn(
        'bg-overlay/60 flex items-center justify-center backdrop-blur-[1px]',
        scope === null ? 'fixed inset-0 z-[70]' : 'absolute inset-0 z-40',
      )}
      role="status"
      aria-busy="true"
      data-busy-overlay
    >
      <BusyPanel key={`${task.id}:${task.hints.join('|')}`} task={task} />
    </div>
  );
}
